import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FileCategoryPicker } from '@/components/documents/FileCategoryPicker';
import type { DocumentRow } from '@/hooks/useDocuments';
import type { PatientFileCategoryRow } from '@/hooks/usePatientFileCategories';
import { fileExtension, localDateInputValue, validateRenamedDisplayName } from '@/utils/fileDisplayName';

function civilDate(value: string | null | undefined): string {
  const day = value?.slice(0, 10) ?? '';
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : localDateInputValue();
}

export function EditPatientFileDialog({
  doc,
  categories,
  pending,
  onClose,
  onSave,
}: {
  doc: DocumentRow;
  categories: readonly PatientFileCategoryRow[];
  pending?: boolean;
  onClose: () => void;
  onSave: (input: {
    originalFilename: string;
    documentDate: string;
    categoryId: string | null;
  }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(doc.original_filename);
  const [date, setDate] = useState(civilDate(doc.document_date));
  const [categoryId, setCategoryId] = useState<string | null>(doc.category_id);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(doc.original_filename);
    setDate(civilDate(doc.document_date));
    setCategoryId(doc.category_id);
    setError(null);
  }, [doc]);

  const renameError = validateRenamedDisplayName(name, doc.original_filename);

  const handleSave = async () => {
    if (renameError) {
      setError(t(`patients.files.rename_${renameError}`));
      return;
    }
    if (!date) {
      setError(t('patients.files.date_required'));
      return;
    }
    setError(null);
    await onSave({ originalFilename: name.trim(), documentDate: date, categoryId });
  };

  return (
    <Modal
      open
      title={t('patients.files.edit_title')}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => void handleSave()} loading={pending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-s4" data-testid="patient-file-edit-dialog">
        <Input
          id="patient-file-display-name"
          label={t('patients.files.display_name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={error && renameError ? t(`patients.files.rename_${renameError}`) : undefined}
          hint={t('patients.files.display_name_hint', { ext: fileExtension(doc.original_filename) })}
          data-testid="patient-file-edit-name"
        />
        <FileCategoryPicker
          id="patient-file-edit-category"
          label={t('patients.files.category')}
          categories={categories}
          value={categoryId}
          onChange={setCategoryId}
        />
        <DanishDateField
          label={t('patients.files.document_date')}
          value={date}
          onChange={(next) => setDate(next || localDateInputValue())}
          calendar
        />
        {error && !renameError ? <p className="text-sm text-ds-red-ink">{error}</p> : null}
      </div>
    </Modal>
  );
}
